// src/components/shared/ui/TablePagination.tsx
import React from "react";

type Props = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Pass when the API reports pages directly; otherwise derived from total. */
  totalPages?: number;
  /** How many numbered buttons to show before the ellipsis. */
  maxPageButtons?: number;
  className?: string;
};

/**
 * The foot of a list card: record range on the left, pager on the right.
 * Sits flush under a table whose bottom corners are square (`rounded-t-2xl`),
 * so the two read as one card.
 */
const TablePagination: React.FC<Props> = ({
  page,
  pageSize,
  total,
  onPageChange,
  totalPages,
  maxPageButtons = 6,
  className = "",
}) => {
  const pages = Math.max(
    1,
    totalPages ?? Math.ceil(total / Math.max(1, pageSize))
  );

  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div
      className={`flex items-center justify-between gap-2 rounded-b-2xl border border-t-0 border-gray-200 bg-white px-4 py-3 ${className}`}
    >
      <div className="text-xs text-gray-500">
        Showing <span className="font-medium text-gray-700">{first}</span> to{" "}
        <span className="font-medium text-gray-700">{last}</span> of{" "}
        <span className="font-medium text-gray-700">{total}</span> results
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Prev
        </button>

        <div className="hidden items-center gap-1 sm:flex">
          {Array.from({ length: Math.min(pages, maxPageButtons) }).map((_, idx) => {
            const n = idx + 1;
            const active = n === page;

            return (
              <button
                key={n}
                type="button"
                onClick={() => onPageChange(n)}
                aria-current={active ? "page" : undefined}
                className={`rounded-md px-2.5 py-1.5 text-sm ${
                  active
                    ? "bg-sky-600 text-white"
                    : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {n}
              </button>
            );
          })}

          {pages > maxPageButtons && (
            <span className="px-1 text-sm text-gray-500">…</span>
          )}
        </div>

        <button
          type="button"
          onClick={() => onPageChange(Math.min(pages, page + 1))}
          disabled={page >= pages}
          className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default TablePagination;
